import {announce} from '@github-ui/aria-live'
import {useCallback, useEffect, useRef} from 'react'

import {looksLikeCode, MAX_PRETTIFY_PASTE_SIZE} from './looks-like-code'
import {usePrettifiedBlocks} from './use-prettified-blocks'

// Read about the first ~100 lines of the paste to decide if it looks like code.
const CODE_DETECTION_SAMPLE = 5_000

function readClipboardText(clipboardData: DataTransfer): string {
  // Mirror the items-based read so synthetic paste events in tests (which expose
  // `items` + `getData`) behave the same as real browser clipboard events.
  const stringItem = Array.from(clipboardData.items ?? []).find(
    item => item.kind === 'string' && (item.type === 'text' || item.type === 'text/plain'),
  )
  return stringItem ? clipboardData.getData(stringItem.type) : clipboardData.getData('text/plain')
}

/**
 * Wires the "prettify pasted code" behavior for the shared ChatComposer: pasted
 * code becomes editable/collapsible blocks that serialize back to fenced code on
 * submit. The returned handlers are inert unless the consumer attaches them, so
 * the composer can keep its default (prettify-off) behavior untouched.
 */
export function usePrettifyComposer(options?: {draftKey?: string; restore?: boolean}) {
  const {
    blocks,
    addBlock,
    removeBlock,
    removeSubmittedBlocks,
    updateBlockCode,
    toggleCollapse,
    clearBlocks,
    getBlocksText,
  } = usePrettifiedBlocks(options)
  const lastActionRef = useRef<'add' | 'remove' | null>(null)

  // Announce add/remove after the blocks state commits so the count reflects the
  // post-update length (avoids the off-by-one stale-closure read of blocks.length).
  useEffect(() => {
    const action = lastActionRef.current
    lastActionRef.current = null
    if (action === 'add') announce(`Code block added, ${blocks.length} total`)
    else if (action === 'remove') announce(`Code block removed, ${blocks.length} remaining`)
  }, [blocks.length])

  const handlePaste = useCallback<React.ClipboardEventHandler<HTMLTextAreaElement>>(
    event => {
      const clipboardData = event.clipboardData
      if (!clipboardData) return
      const pastedText = readClipboardText(clipboardData)
      // Above this size we fall back to a normal plain-text paste; attaching large pastes as a file is intentionally not supported here.
      if (
        pastedText.length > 0 &&
        pastedText.length <= MAX_PRETTIFY_PASTE_SIZE &&
        looksLikeCode(pastedText.slice(0, CODE_DETECTION_SAMPLE))
      ) {
        event.preventDefault()
        addBlock(pastedText)
        lastActionRef.current = 'add'
      }
    },
    [addBlock],
  )

  const removeBlockWithStatus = useCallback(
    (id: string) => {
      removeBlock(id)
      lastActionRef.current = 'remove'
    },
    [removeBlock],
  )

  // Removes the most recently added block (e.g. Backspace on an empty composer).
  const popLastBlock = useCallback(() => {
    const last = blocks[blocks.length - 1]
    if (last) removeBlockWithStatus(last.id)
  }, [blocks, removeBlockWithStatus])

  // Prepend the prettified blocks (as fenced code) to the submitted text. Reads
  // the blocks; the caller clears them on a successful submit.
  const serialize = useCallback(
    (text: string): string => [getBlocksText(), text].filter(Boolean).join('\n'),
    [getBlocksText],
  )

  return {
    blocks,
    hasBlocks: blocks.length > 0,
    handlePaste,
    popLastBlock,
    serialize,
    clearBlocks,
    removeSubmittedBlocks,
    onRemoveBlock: removeBlockWithStatus,
    onToggleCollapse: toggleCollapse,
    onCodeChange: updateBlockCode,
  }
}
